import React, { useState } from 'react';
import './stylesheets/ApiTest.css';

const ApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [rawResponse, setRawResponse] = useState<string | null>(null); // formatted JSON or text
  const [summary, setSummary] = useState<null | {
    totalEndpoints: number;
    healthyEndpoints: number;
    unhealthyEndpoints: number;
    duration: string;
    testResults: Array<{
      name: string;
      method: string;
      healthy: boolean;
      responseTime: number;
      statusCode: number;
    }>;
  }>(null);
  const [error, setError] = useState<string | null>(null);

  // Prefer environment variable so environments can override without code change
  const endpoint = process.env.REACT_APP_HEALTHCHECK_URL || 'https://livemockmedichealth.azurewebsites.net/api/healthCheckTest';

  const callApi = async () => {
    setLoading(true);
    setRawResponse(null);
    setSummary(null);
    setError(null);
    try {
      const res = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json, text/plain, */*'
        }
      });
      const contentType = res.headers.get('content-type') || '';
      let body: any;
      if (contentType.includes('application/json')) {
        body = await res.json();
      } else {
        body = await res.text();
      }
      if (!res.ok) {
        throw new Error(typeof body === 'string' ? body : JSON.stringify(body));
      }
      if (typeof body === 'string') {
        setRawResponse(body);
      } else {
        // Try extract structured health shape
        if (body && body.summary && Array.isArray(body.summary.testResults)) {
          setSummary({
            totalEndpoints: body.summary.totalEndpoints,
            healthyEndpoints: body.summary.healthyEndpoints,
            unhealthyEndpoints: body.summary.unhealthyEndpoints,
            duration: body.summary.duration,
            testResults: body.summary.testResults
          });
        }
        setRawResponse(JSON.stringify(body, null, 2));
      }
    } catch (e: any) {
      const msg = e?.message || 'Unknown error';
      if (e instanceof TypeError) {
        // Likely network / CORS / certificate in browser context
        setError(`${msg}\n(Hint: If this is a CORS issue, verify Access-Control-Allow-Origin includes ${window.location.origin})`);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="operation-content">
      <h2>API Test</h2>
      <p style={{ marginTop: '-10px', marginBottom: '20px', color: '#888' }}>Call the health check endpoint and inspect the raw response.</p>
      <div className="settings-section" style={{ maxWidth: 800 }}>
        <h3 style={{ marginTop: 0 }}>Health Check</h3>
        <div className="settings-item" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <code className="api-endpoint-code">{endpoint}</code>
          <button className="btn-primary" onClick={callApi} disabled={loading}>
            {loading ? 'Calling...' : 'Call API'}
          </button>
          <button className="btn-secondary" onClick={() => { setRawResponse(null); setSummary(null); setError(null); }} disabled={loading}>Clear</button>
        </div>
        <div style={{ marginTop: '20px' }}>
          {error && (
            <div style={{ color: '#ff5252', marginBottom: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
              Error: {error}
            </div>
          )}
          {summary && (
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ margin: '10px 0 6px' }}>Summary</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="api-table">
                  <thead>
                    <tr style={{ background: '#1e1e1e' }}>
                      <th style={thStyle}>Total</th>
                      <th style={thStyle}>Healthy</th>
                      <th style={thStyle}>Unhealthy</th>
                      <th style={thStyle}>Duration</th>
                      <th style={thStyle}>All Healthy?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={tdStyle}>{summary.totalEndpoints}</td>
                      <td style={tdStyle}>{summary.healthyEndpoints}</td>
                      <td style={tdStyle}>{summary.unhealthyEndpoints}</td>
                      <td style={tdStyle}>{summary.duration}</td>
                      <td style={tdStyle}>{summary.unhealthyEndpoints === 0 ? '✅' : '❌'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <h4 style={{ margin: '18px 0 6px' }}>Endpoint Details</h4>
              <div style={{ overflowX: 'auto' }}>
                <table className="api-table">
                  <thead>
                    <tr style={{ background: '#1e1e1e' }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Method</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Healthy</th>
                      <th style={thStyle}>Response Time (ms)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.testResults.map(r => (
                      <tr key={r.name}>
                        <td style={tdStyle}>{r.name}</td>
                        <td style={tdStyle}>{r.method}</td>
                        <td style={tdStyle}>{r.statusCode}</td>
                        <td style={tdStyle}>{r.healthy ? '✅' : '❌'}</td>
                        <td style={tdStyle}>{r.responseTime}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Derived stats */}
              {summary.testResults.length > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#aaa', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                  {(() => {
                    const times = summary.testResults.map(r => r.responseTime);
                    const min = Math.min(...times);
                    const max = Math.max(...times);
                    const fastest = summary.testResults.find(r => r.responseTime === min)?.name;
                    const slowest = summary.testResults.find(r => r.responseTime === max)?.name;
                    return `Fastest: ${fastest} (${min} ms) | Slowest: ${slowest} (${max} ms) | Spread: ${max - min} ms`;
                  })()}
                </div>
              )}
            </div>
          )}
          {rawResponse && (
            <details open>
              <summary style={{ cursor: 'pointer', fontSize: 13, color: '#ccc' }}>Raw Response</summary>
              <pre className="api-raw-response">{rawResponse}</pre>
            </details>
          )}
          {!rawResponse && !summary && !error && !loading && (
            <div style={{ color: '#666', fontStyle: 'italic' }}>No response yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Table cell styles (outside component to avoid reallocation)
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 8px',
  borderBottom: '1px solid #333',
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: 0.3
};

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  borderBottom: '1px solid #222',
  fontSize: 12
};

export default ApiTest;